import { TabContext, TabList } from "@mui/lab";
import {
  Box,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  type SxProps,
  Tab,
  Typography,
} from "@mui/material";
import { isEmpty } from "lodash-es";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CustomTemplates } from "../../pages/create-content/use-custom-templates";
import { useGenerateCommsContext } from "../template-preview/generate-comms-context";
import { ImageShapeSelect } from "./image-shape-select";
import { VisualStyleSelect } from "./visual-style-select";

export const CommsCustomStyleSelect: FC<{
  customTemplates: CustomTemplates;
}> = ({ customTemplates }) => {
  const {
    branding: { selectedStyle, selectedImageType, setBrandingStyle, setBrandingImageType },
  } = useGenerateCommsContext();

  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState<"custom" | "standard" | null>("custom");

  const squares = useMemo(() => customTemplates?.["Square Comms"] ?? [], [customTemplates]);
  const banners = useMemo(() => customTemplates?.["Landscape Comms"] ?? [], [customTemplates]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedTab(selectedStyle?.branding_type?.type === "standard" ? "standard" : "custom");
  }, [selectedStyle?.branding_type?.type]);

  const selectedTemplate = useMemo(() => {
    if (selectedStyle?.branding_type?.type === "standard") {
      return {
        name: selectedImageType === "banner" ? "Banner" : "Square",
        id: selectedImageType,
      };
    }

    const templates = [...squares, ...banners];
    const template = templates.find(
      (_template) => _template.template.template_code === selectedStyle?.branding_type?.value,
    );
    return {
      name: template?.name,
      id: template?.template.template_code,
    };
  }, [
    banners,
    selectedStyle?.branding_type?.type,
    selectedStyle?.branding_type?.value,
    selectedImageType,
    squares,
  ]);

  const handleSelectStandard = useCallback(
    (type: "square" | "banner") => () => {
      const imageStyle =
        selectedStyle?.branding_type?.type === "standard"
          ? selectedStyle?.branding_type?.value
          : "bold";
      setBrandingStyle({ type: "standard", value: imageStyle });
      setBrandingImageType(type);
    },
    [
      selectedStyle?.branding_type?.type,
      selectedStyle?.branding_type?.value,
      setBrandingStyle,
      setBrandingImageType,
    ],
  );

  const handleSelectCustom = useCallback(
    (type: "square" | "banner", template_code: string | null) => () => {
      if (!template_code) return;
      setBrandingStyle({ type: "custom", value: template_code });
      setBrandingImageType(type);
    },
    [setBrandingStyle, setBrandingImageType],
  );

  const maybeHideSx = (tab: "custom" | "standard"): SxProps => ({
    ...(selectedTab === tab ? {} : { height: 0, overflow: "hidden", padding: 0, margin: 0 }),
  });

  const selectRef = useRef<HTMLDivElement>(null);

  if (!selectedStyle?.branding_type) return null;
  return (
    <Stack direction="row" gap={2}>
      {isEmpty(squares) && isEmpty(banners) ? (
        <>
          <ImageShapeSelect onChange={setBrandingImageType} imageType={selectedImageType} />
          <VisualStyleSelect onChange={setBrandingStyle} brandStyle={selectedStyle.branding_type} />
        </>
      ) : (
        <>
          {selectedTemplate && (
            <TabContext value={selectedTab || "custom"}>
              <FormControl
                onKeyDown={(e) => {
                  if (e.code === "ArrowDown" || e.code === "ArrowUp") {
                    setTimeout(() => {
                      const focussed =
                        selectRef.current?.getElementsByClassName("Mui-focusVisible")[0];
                      if (focussed?.clientHeight === 0) {
                        setSelectedTab(selectedTab === "custom" ? "standard" : "custom");
                      }
                    }, 100);
                  }
                }}
              >
                <InputLabel htmlFor="custom-style-select">{t("Image shape")}</InputLabel>
                <Select
                  id="custom-style-select"
                  label={t("Image shape")}
                  inputProps={{ "aria-label": t("Image shape") }}
                  value={selectedTemplate.id}
                  native={false}
                  sx={{ width: "300px" }}
                  renderValue={() => (
                    <Typography variant="body1" color="text.primary">
                      {selectedTemplate?.name}
                    </Typography>
                  )}
                  MenuProps={{ ref: selectRef }}
                >
                  <Box
                    p={1}
                    onClick={(e) => {
                      e.stopPropagation();
                      return false;
                    }}
                  >
                    <TabList
                      sx={{ paddingLeft: 1, border: "none" }}
                      onChange={(_, val: "custom" | "standard") => setSelectedTab(val)}
                    >
                      <Tab label="Your designs" value="custom" />
                      <Tab label="Standard" value="standard" />
                    </TabList>
                  </Box>

                  {!isEmpty(squares) && (
                    <ListSubheader sx={maybeHideSx("custom")}>{t("Squares")}</ListSubheader>
                  )}
                  {squares?.map(({ name, template: { template_code } }) => (
                    <MenuItem
                      key={template_code}
                      onClick={handleSelectCustom("square", template_code)}
                      value={template_code!}
                      selected={
                        selectedStyle?.branding_type?.type === "custom" &&
                        selectedStyle?.branding_type?.value === template_code
                      }
                      sx={maybeHideSx("custom")}
                    >
                      {name}
                    </MenuItem>
                  ))}
                  {!isEmpty(banners) && (
                    <ListSubheader sx={maybeHideSx("custom")}>{t("Banners")}</ListSubheader>
                  )}

                  {banners?.map(({ name, template: { template_code } }) => (
                    <MenuItem
                      key={template_code}
                      value={template_code!}
                      onClick={handleSelectCustom("banner", template_code)}
                      selected={
                        selectedStyle?.branding_type?.type === "custom" &&
                        selectedStyle?.branding_type?.value === template_code!
                      }
                      sx={maybeHideSx("custom")}
                    >
                      {name}
                    </MenuItem>
                  ))}

                  <MenuItem
                    value="square"
                    onClick={handleSelectStandard("square")}
                    selected={
                      selectedStyle?.branding_type?.type === "standard" &&
                      selectedImageType === "square"
                    }
                    sx={maybeHideSx("standard")}
                  >
                    {t("Square")}
                  </MenuItem>
                  <MenuItem
                    value="banner"
                    onClick={handleSelectStandard("banner")}
                    selected={
                      selectedStyle?.branding_type?.type === "standard" &&
                      selectedImageType === "banner"
                    }
                    sx={maybeHideSx("standard")}
                  >
                    {t("Banner")}
                  </MenuItem>
                </Select>
              </FormControl>
            </TabContext>
          )}
          {selectedStyle?.branding_type?.type === "standard" && (
            <VisualStyleSelect
              onChange={setBrandingStyle}
              brandStyle={selectedStyle.branding_type}
            />
          )}
        </>
      )}
    </Stack>
  );
};
